import { Component, Inject } from "@angular/core";
import { Organization } from "../../../common/models/organization";
import { User } from "../../../common/models/user";
import { MobileSessionViewComponent } from "../../../mobile/diary/session-view/mobile.session-view.component";
import { ModuleContentComponent } from "../../../common/components/module-content/module-content";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { Session } from "../../../common/models/session";
import { TranslateModule } from "@ngx-translate/core";
import { MatButton, MatButtonModule } from "@angular/material/button";
import { Timeslot } from "../../../common/models/timeslot";
import { Library } from "../../../common/models/library";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { MatFormField, MatFormFieldModule } from "@angular/material/form-field";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatInput } from "@angular/material/input";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { NgIf } from "@angular/common";
import moment from "moment";
import { SessionRow, SessionsProvider } from "../../../sessions/providers/sessions-provider";
import { SessionItemsProvider } from "../../../sessions/providers/sessionItems-provider";
import { SessionTimeSlotRow, TimeslotsProvider } from "../../providers/timeslots-provider";
import { forkJoin, map, Observable, switchMap } from "rxjs";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { Agenda } from "../../../common/models/agenda";
import { catchError } from "rxjs/operators";

@Component({
  selector: 'agenda-mobile-session-display-dialog',
  styleUrls: ['./agenda-mobile-session-display-dialog.scss'],
  standalone: true,
  templateUrl: './agenda-mobile-session-display-dialog.html',
  imports: [
    MobileSessionViewComponent,
    ModuleContentComponent,
    MatDialogModule,
    TranslateModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInput,
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerToggle,
    NgIf,
  ],
  providers: []
})
export class AgendaMobileSessionDisplayDialog {
  public organization: Organization = {} as Organization;
  public session: Session | null = null;
  public athleteUser: User = {} as User;
  public sessionTimeslotRow: SessionTimeSlotRow = {} as SessionTimeSlotRow;

  public error: string | null = null;

  public library: Library = {} as Library;
  public timeslotId: number;

  public timeslotFormGroup: FormGroup | undefined;

  constructor(
    private fb: FormBuilder,
    private sessionsProvider: SessionsProvider,
    private timeslotsProvider: TimeslotsProvider,
    private utils: UtilsProvider,
    public dialog: MatDialogRef<AgendaMobileSessionDisplayDialog>,
    @Inject(MAT_DIALOG_DATA) private _data :any
  ) {
    if (!this._data) {
      throw new Error('No data provided');
    }
    if (!this._data.sessionTimeslotRow) {
      throw new Error('No sessionTimeslotRow provided');
    }
    if (!this._data.organization) {
      throw new Error('No organization provided');
    }
    if (!this._data.athleteUser) {
      throw new Error('No athlete User provided')
    }
    if (!this._data.library) {
      throw new Error('No library provided')
    }

    this.organization = this._data.organization;
    this.sessionTimeslotRow = this._data.sessionTimeslotRow;
    this.timeslotId = this.sessionTimeslotRow.timeslotId;
    this.athleteUser = this._data.athleteUser;
    this.library = this._data.library;
    this.error = this._data.error;

    const date =  moment(this.sessionTimeslotRow.fromDate ?? new Date()).toDate();
    const timeStart = utils.DateToHm(date);

    this.timeslotFormGroup = this.fb.group({
      date: new FormControl(date, [Validators.required]),
      timeStart: new FormControl(timeStart, {
        validators: [Validators.required, this.timeStartValidator()],
        updateOn: 'blur',
      }),
      timeEnd: new FormControl({value: "00:00", disabled: true}, []),
    });

    this.updateTimeEnd();

    this.sessionsProvider.getSessionByTimeslotId(this.timeslotId).subscribe({
      next: (session) => {
        this.session = session;
      },
    });
  }

  get f() {
    return this.timeslotFormGroup!.controls;
  }

  private updateTimeEnd() {
    const date = moment(this.timeslotFormGroup!.controls["date"].value).format('YYYY-MM-DD');
    const timeStart = this.timeslotFormGroup!.controls["timeStart"].value;
    const duration = this.sessionTimeslotRow.totalTime;

    if (date && timeStart && duration) {
      const timeEnd = this.utils.DateToHm(moment(`${date} ${timeStart}`, 'YYYY-MM-DD HH:mm').add(duration, 'seconds').toDate());
      this.timeslotFormGroup!.controls["timeEnd"].setValue(timeEnd, { emitEvent: false });
    }
  }


  private timeStartValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let originalValue = control.value;
      let timeValue = originalValue;
      //interpréter l'heure de début si invalide
      switch (timeValue.length) {
        case 4:
          timeValue = `${timeValue.substring(0, 2)}:${timeValue.substring(2, 4)}`;
          break;
        case 3:
          timeValue = `0${timeValue.substring(0, 1)}:${timeValue.substring(1, 3)}`;
          break;
        case 2:
          timeValue = `${timeValue}:00`;
          break;
        case 1:
          timeValue = `0${timeValue}:00`;
          break;
      }
      // si l'heure de début est invalide
      if (!moment(timeValue, 'HH:mm', true).isValid()) {
        return { invalidTime: true };
      }


      // Round to the nearest half hour
      let timeMoment = moment(timeValue, 'HH:mm');
      const minutes = timeMoment.minutes();

      if (minutes < 15) {
        timeMoment.minutes(0);
      } else if (minutes < 45) {
        timeMoment.minutes(30);
      } else {
        timeMoment.add(1, 'hour').minutes(0);
      }

      // Set the rounded time back into the form control
      // only if different from the original value
      if (timeMoment.format('HH:mm') == originalValue) {
        return null;
      }
      control.setValue(timeMoment.format('HH:mm'), { emitEvent: false });

      // Call the updateTimeEnd function after validation
      this.updateTimeEnd();

      return null; // No validation error if time is valid
    };
  }

  public onSaveClicked() {
    const date = moment(this.timeslotFormGroup!.controls["date"].value).format('YYYY-MM-DD');
    const timeStart = this.timeslotFormGroup!.controls["timeStart"].value;
    const newDate = `${date} ${timeStart}:00`;
    this.timeslotsProvider.updateTimeslotDate({id: this.timeslotId, date: newDate}).pipe(
      map((response) => {
        if (response.success) {
          this.dialog.close(response.timeslot);
        } else {
          if (response.error == "validation") {
            this.error = "Timeslot_overlap_error";
          } else {
            this.error = "Timeslot_unknown_error";
          }
        }
      }),
    ).subscribe();
  }
}
